import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";
import {
  AlertTriangle,
  Thermometer,
  ShieldCheck,
  Activity,
  Flame,
  CheckCircle2,
} from "lucide-react";

type OverviewData = {
  stats: {
    active_windrows: number;
    alert_count: number;
    avg_temp: number;
  };
  temp_alerts: Array<{
    row_id: string;
    name: string;
    temperature: number;
    recorded_at: string;
  }>;
  recent_activity: Array<{
    type: string;
    who: string;
    windrow: string;
    detail: string;
    ts: string;
  }>;
};

export default function OperatorStatus() {
  const { token } = useAuth();
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch("/api/operations/overview", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-card border border-border rounded-xl p-5 animate-pulse">
            <div className="h-4 bg-muted rounded w-40 mb-3" />
            <div className="h-10 bg-muted rounded w-20" />
          </div>
        ))}
      </div>
    );
  }

  const stats = data?.stats;
  const alertRows = data?.temp_alerts?.filter((a) => a.temperature < 131) || [];
  const todayActivity = data?.recent_activity || [];
  const tempsToday = todayActivity.filter((a) => a.type === "temperature").length;
  const turnsToday = todayActivity.filter((a) => a.type === "turn").length;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="heading-display text-2xl text-foreground">Status</h1>
        <p className="text-xs text-muted-foreground mt-0.5">At-a-glance facility overview</p>
      </div>

      {/* Alert / All Clear banner */}
      {alertRows.length > 0 ? (
        <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center shrink-0">
              <AlertTriangle size={22} className="text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-sm font-bold text-red-700 dark:text-red-300">
                {alertRows.length} row{alertRows.length !== 1 ? "s" : ""} need attention
              </p>
              <p className="text-xs text-red-600/70 dark:text-red-400/70 mt-0.5">
                Below 131°F compliance threshold
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-forest-50 dark:bg-forest-900/10 border border-forest-200 dark:border-forest-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-forest-100 dark:bg-forest-900/30 rounded-xl flex items-center justify-center shrink-0">
              <ShieldCheck size={22} className="text-forest-600 dark:text-forest-400" />
            </div>
            <div>
              <p className="text-sm font-bold text-forest-700 dark:text-forest-300">
                All clear
              </p>
              <p className="text-xs text-forest-600/70 dark:text-forest-400/70 mt-0.5">
                All rows within compliance
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Quick stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-card border border-border rounded-xl p-4 text-center">
          <Flame size={20} className="mx-auto text-amber-500 mb-2" />
          <p className="stat-value text-2xl text-foreground">{stats?.avg_temp || 0}°</p>
          <p className="text-[10px] text-muted-foreground mt-1 uppercase tracking-wide font-semibold">Avg Temp</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 text-center">
          <Thermometer size={20} className="mx-auto text-forest-500 mb-2" />
          <p className="stat-value text-2xl text-foreground">{tempsToday}</p>
          <p className="text-[10px] text-muted-foreground mt-1 uppercase tracking-wide font-semibold">Temps Logged</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 text-center">
          <Activity size={20} className="mx-auto text-blue-500 mb-2" />
          <p className="stat-value text-2xl text-foreground">{turnsToday}</p>
          <p className="text-[10px] text-muted-foreground mt-1 uppercase tracking-wide font-semibold">Turns Today</p>
        </div>
      </div>

      {/* Alert details */}
      {alertRows.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-4">
          <h2 className="heading-display text-base text-foreground mb-3">Rows Below Threshold</h2>
          <div className="space-y-2">
            {alertRows.map((alert) => (
              <div
                key={alert.row_id}
                className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/50 rounded-lg"
              >
                <div>
                  <p className="text-sm font-semibold text-foreground">{alert.name}</p>
                  <p className="text-xs text-muted-foreground font-mono">{alert.row_id}</p>
                </div>
                <div className="text-right">
                  <p className="stat-value text-xl text-red-600 dark:text-red-400">
                    {alert.temperature}°F
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent activity */}
      <div className="bg-card border border-border rounded-xl p-4">
        <h2 className="heading-display text-base text-foreground mb-3">Recent Activity</h2>
        {todayActivity.length > 0 ? (
          <div className="space-y-2">
            {todayActivity.slice(0, 8).map((item, i) => (
              <div key={i} className="flex items-center gap-3 py-2 border-b border-border/50 last:border-0">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                  item.type === "temperature"
                    ? "bg-amber-100 dark:bg-amber-900/20"
                    : "bg-blue-100 dark:bg-blue-900/20"
                }`}>
                  {item.type === "temperature" ? (
                    <Thermometer size={14} className="text-amber-600 dark:text-amber-400" />
                  ) : (
                    <CheckCircle2 size={14} className="text-blue-600 dark:text-blue-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-foreground">
                    <span className="font-semibold">{item.detail}{item.type === "temperature" ? "°F" : ""}</span>
                    <span className="text-muted-foreground"> on </span>
                    <span className="font-medium">{item.windrow}</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6">
            <Activity size={24} className="mx-auto text-muted-foreground/30 mb-2" />
            <p className="text-xs text-muted-foreground">No recent activity</p>
          </div>
        )}
      </div>
    </div>
  );
}
